import axios, {
  AxiosError,
  type AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { HOST_URL } from "config";
import { PATH } from "routes/paths";
import type { CustomResponseFormat } from "api/instance";

// 앰버서더(인플루언서) 대시보드 API.
// 앰버서더 토큰은 유저 인증(X-Auth-Token/유저 Bearer)과 완전 별개 체계라 기존 두 인스턴스를 쓰지 않는다 —
// 각 인스턴스의 401 처리(/login·/promotion-login 리다이렉트, 토큰 리프레쉬)가 앰버서더 플로우와 맞지 않기 때문.

// 키 문자열은 유지한다 — 바꾸면 기존 로그인 세션이 전부 로그아웃된다.
export const AMBASSADOR_TOKEN_STORAGE_KEY = "AMBASSADOR_TOKEN";

export const getAmbassadorToken = () =>
  localStorage.getItem(AMBASSADOR_TOKEN_STORAGE_KEY);
export const setAmbassadorToken = (token: string) =>
  localStorage.setItem(AMBASSADOR_TOKEN_STORAGE_KEY, token);
export const clearAmbassadorToken = () =>
  localStorage.removeItem(AMBASSADOR_TOKEN_STORAGE_KEY);

export const AMBASSADOR_DOMAIN = "ambassador";

export const AMBASSADOR_SIGNUP_ENDPOINT = "signup";
export const AMBASSADOR_VERIFY_ENDPOINT = "verify";
export const AMBASSADOR_LOGIN_ENDPOINT = "login";
export const AMBASSADOR_RESEND_ENDPOINT = "verification/resend";
export const AMBASSADOR_DASHBOARD_ENDPOINT = "dashboard";
export const AMBASSADOR_PASSWORD_CHANGE_ENDPOINT = "password/change";
export const AMBASSADOR_CONTRACT_ENDPOINT = "contract";
export const AMBASSADOR_CODE_CREATE_ENDPOINT = "code/create";
export const AMBASSADOR_NAME_ENDPOINT = "name";

export type AmbassadorTokenResult = {
  accessToken: string;
  // 활동명 (계약 제출 전이면 null)
  name?: string | null;
};

export type AmbassadorStatsSummary = {
  signupCount: number;
  payerCount: number;
  refundCount: number;
  // 정산액(원) — 그 달 적용 단가(결제자 1인당 정액)가 있는 monthly/total 에만 채워진다. daily 는 항상 null.
  settlementAmount?: number | null;
};

// period 는 daily 면 "2026-08-03", monthly 면 "2026-08" (서버 최신순 정렬)
export type AmbassadorStatsBucket = AmbassadorStatsSummary & {
  period: string;
};

// 가입자 = 링크로 유입되어 코드가 귀속된 유저, 결제자 = 정산 기여 확정 유저, 환불자 = 그중 결제 취소 유저
export type AmbassadorDashboardResult = {
  // 활동명 (계약 제출 전이면 null)
  name?: string | null;
  // 로그인 계정 전화번호, 숫자만 "01012345678" (계정 관리 메뉴 표시·계약 폼 프리필용)
  phoneNumber?: string | null;
  codes: string[];
  // 코드별 승인(활성) 상태 — 미승인 코드는 링크 복사를 막고 승인 대기로 표시
  codeInfos?: { code: string; isActive: boolean }[];
  total: AmbassadorStatsSummary;
  daily: AmbassadorStatsBucket[];
  monthly: AmbassadorStatsBucket[];
};

// 계약형태 — 발송할 계약서 폼 구분 (서버 AmbassadorContractTypeConstants 와 페어)
export type AmbassadorContractType =
  | "freelancer"
  | "business_general"
  | "business_simplified";

// 계약 정보 — type 이 null 이면 미제출. 파일은 base64 data URL 원문. name(활동명)은 계정 단일 소스.
export type AmbassadorContractResult = {
  // 계약 완료 여부 (어드민이 체결 확인 후 표시) — 완료 전에는 셀프 링크를 만들 수 없다
  completed?: boolean;
  type?: AmbassadorContractType | null;
  realName?: string | null;
  businessName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  name?: string | null;
  bankbookFile?: string | null;
  businessRegistrationFile?: string | null;
  // 체결된 계약서 사본 — 어드민이 계약 완료 처리 시 업로드 (계약 당사자 다운로드·미리보기용)
  contractFile?: string | null;
};

// 계약 정보 제출 — 폼 전체 상태 저장 (null 필드 = 비움). 형태별 필수값은 서버가 검증한다.
export type AmbassadorContractParam = {
  type: AmbassadorContractType;
  realName?: string | null;
  businessName?: string | null;
  email: string;
  phoneNumber: string;
  name: string;
  bankbookFile?: string | null;
  businessRegistrationFile?: string | null;
};

interface AmbassadorInstance extends AxiosInstance {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

export const ambassadorAxios: AmbassadorInstance = axios.create({
  baseURL: HOST_URL,
  timeout: 15000, // 15초 타임아웃
});

// 매 요청 시점에 localStorage 를 읽어 토큰을 부착한다 (로그인 직후/다른 탭 갱신에 의한 stale 방지).
const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const token = getAmbassadorToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// 비즈니스 오류는 HTTP 200 + code≠0 이므로 envelope 를 그대로 반환한다 — code 검사는 호출부 책임.
const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response.data;
};

// 앰버서더 토큰 무효/만료(HTTP 401)면 토큰을 지우고 로그인으로 보낸다. 로그인 페이지에서는 리다이렉트하지 않는다(루프 방지).
// 하드 리다이렉트라 라우터 state 를 못 쓰므로 원래 위치를 ?returnTo= 로 실어 로그인 후 복귀하게 한다.
const onRejected = (error: AxiosError) => {
  if (error.response?.status === 401) {
    clearAmbassadorToken();
    if (window.location.pathname !== PATH.ambassadorLogin) {
      const returnTo = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      window.location.href = `${PATH.ambassadorLogin}?returnTo=${returnTo}`;
    }
  }
  return Promise.reject(error);
};

ambassadorAxios.interceptors.request.use(onRequest);
ambassadorAxios.interceptors.response.use(onResponse, onRejected);

export const ambassadorApi = {
  // 오픈 가입 (전화번호는 숫자만 "01012345678") — 성공 시 알림톡으로 인증번호 발송
  signup: (phoneNumber: string, password: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_SIGNUP_ENDPOINT}`,
      { phoneNumber, password }
    ),

  // 전화번호 인증 — 성공 시 토큰 발급(자동 로그인).
  // 비밀번호를 함께 재검증한다 (미인증 계정을 제3자 signup 이 덮어써 탈취하는 것 차단 — 서버 정책).
  verify: (phoneNumber: string, password: string, code: string) =>
    ambassadorAxios.post<CustomResponseFormat<AmbassadorTokenResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_VERIFY_ENDPOINT}`,
      {
        phoneNumber,
        password,
        code,
      }
    ),

  login: (phoneNumber: string, password: string) =>
    ambassadorAxios.post<CustomResponseFormat<AmbassadorTokenResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_LOGIN_ENDPOINT}`,
      {
        phoneNumber,
        password,
      }
    ),

  resendVerification: (phoneNumber: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_RESEND_ENDPOINT}`,
      { phoneNumber }
    ),

  dashboard: () =>
    ambassadorAxios.get<CustomResponseFormat<AmbassadorDashboardResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_DASHBOARD_ENDPOINT}`
    ),

  // 로그인 상태에서 비밀번호 변경 — 현재 비밀번호 재검증 (계정 관리 메뉴)
  changePassword: (currentPassword: string, newPassword: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_PASSWORD_CHANGE_ENDPOINT}`,
      { currentPassword, newPassword }
    ),

  // 계약 정보 조회 — type 이 null 이면 미제출
  getContract: () =>
    ambassadorAxios.get<CustomResponseFormat<AmbassadorContractResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CONTRACT_ENDPOINT}`
    ),

  // 계약 정보 제출 — 폼 전체 상태 저장, 파일은 base64 data URL (5MB 이하)
  submitContract: (param: AmbassadorContractParam) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CONTRACT_ENDPOINT}`,
      param
    ),

  // 레퍼럴 링크 생성 — 슬러그는 영문 소문자·숫자·하이픈 3~20자, 1인 1코드. 노출 이름은 활동명(계정 name)을 쓴다
  createCode: (code: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CODE_CREATE_ENDPOINT}`,
      { code }
    ),

  // 활동명 수정 — 링크 관리 메뉴에서 노출 이름 갱신 (계정 name + 본인 코드 이름 동기화)
  updateName: (name: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_NAME_ENDPOINT}`,
      { name }
    ),
};
